import { STRIPE_MINIMUM_CARD_CHARGE } from "./static"

const STRIPE_MINIMUM_CARD_CHARGE_CENTS = Math.round(
  STRIPE_MINIMUM_CARD_CHARGE * 100,
)

/**
 * Returns the customer-balance adjustment Stripe will apply to an invoice, in
 * cents: negative reduces the amount due (credit), positive increases it
 * (debit). Lets a checkout show the same total Stripe ends up charging.
 *
 * @param balanceCents Stripe customer balance (negative = credit on account)
 * @param totalCents Invoice total before the balance is applied
 */
export const calculateStripeBalanceAdjustmentCents = ({
  balanceCents,
  totalCents,
}: {
  balanceCents: number
  totalCents: number
}): number => {
  if (balanceCents >= 0) {
    return balanceCents
  }

  // Stripe never charges a card below its minimum: it settles the invoice from
  // the balance and rolls the shortfall over as a debit on the next one, so the
  // credit effectively covers the whole total.
  return totalCents + balanceCents < STRIPE_MINIMUM_CARD_CHARGE_CENTS
    ? -totalCents
    : balanceCents
}

/**
 * A user's credit, split by the Stripe customer that holds it. The two
 * balances cannot be pooled: Stripe applies a customer's balance only to that
 * customer's invoices, and post-payment invoices carrying a billing period are
 * issued to a dedicated customer.
 */
export interface UserCredits {
  /**
   * Applies to card-charged and pre-payment invoices, and to bulk pay-later
   * ones — those carry no billing period and stay on the primary customer.
   */
  amount: number
  /**
   * Applies only to post-payment invoices carrying a billing period, the ones
   * stamped with the "Post-Payment Billing Period" field. Null when the user
   * has no post-payment customer, which is every user who is not post-payment
   * enabled.
   */
  postPaymentAmount: number | null
}

/** Which of a user's two Stripe balances a credit or deduction targets. */
export type CreditTarget = "primaryStripeCustomer" | "postPaymentStripeCustomer"
